import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { PetEntity } from 'src/game/logic/EntityManager';
import { PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { gsap } from 'gsap';
import { now, user } from 'src/StateObserver';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  ImageCoverView,
  ImageScaleView,
  ImageView,
} from '@play-co/timestep-core/ui';
import {
  FlexBoxImageScaleView,
  FlexBoxView,
} from '@play-co/timestep-core/flexbox';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { animDuration, delay, waitForItPromise } from 'src/lib/utils';
import Match2LiveOpsProgressbar from './Match2LiveOpsProgresbar';
import {
  getMatch2FrenzyEventConfig,
  getMatch2FrenzyMilestone,
  getMatch2FrenzyMilestoneProgress,
  isMatch2FrenzyEnabled,
} from 'src/replicant/match2/frenzy/match2-frenzy.getters';
import { getFrenzyObjectiveIcon, getStreakRewardIcon } from '../match2.icons';
import {
  getCurrentBuruStreakRewardSet,
  isMatch2UnlockableUnlocked,
} from 'src/replicant/match2/match2.getters';
import { match2BuruStreakRewards } from 'src/replicant/match2/match2.ruleset';
import {
  getStreakBattleRoyaleCurrentMilestone,
  getStreakBattleRoyaleGoal,
  isStreakBattleRoyaleActive,
} from 'src/replicant/streakbattleroyale/streakbattleroyale.getters';
import { GemsWallet } from 'src/game/components/GemsWallet';
import { streakBattleRoyaleTheme } from 'src/game/streakbattleroyale/streakbattleroyale.theme';
import { movesPinchCheck } from 'src/lib/pinch';
import {
  getMatch2CloseButtonX,
  match2CloseButtonY,
} from './match2PopupCloseButton';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (result: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupMatch2MovePinchRetry extends PopupBasic {
  private close: CloseHandler;
  private contentWrapper: View;
  private ctaButton: ButtonScaleViewWithText;
  private gemsWallet: GemsWallet;
  private pet: PetEntity;
  private species: PetSpeciesID;
  private achievements: FlexBoxView;
  private toggleMessage?: LangBitmapFontTextShadowView;
  private isPopupHidden = false;

  private bubble: ImageScaleView;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = 676;
    const height = 900;
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      ...ui.popup,
      darkerBg: true,
      offsetY: -50,
      width,
      height,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_POPUP,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();
    this.message.removeFromSuperview();
    this.title.title.updateOpts({ color: '#E25959' });
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.95)',
    });
    this.bg.onClick = () => this.toggleShowPuzzle();

    this.gemsWallet = new GemsWallet({
      superview: this.root,
      tracking: { feature: 'header', $subFeature: 'buy-gems' },
    });

    new ButtonScaleView({
      ...uiConfig.buttons.close,
      superview: this.box,
      x: getMatch2CloseButtonX(this.box.style.width),
      y: match2CloseButtonY,
      onClick: async () => {
        sounds.playSound('cancel');
        return opts.close(false);
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_BACK,
      },
    });

    this.contentWrapper = new View({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
    });

    this.species = Pet.getters.getCurrentEvolution(user())?.species ?? 'ball';
    this.pet = new PetEntity('minigame', this.species);
    this.pet.updateOpts({
      superview: this.contentWrapper,
      x: 145,
      y: 300,
      scale: 0.75,
      anchorX: 0.5 * this.pet.style.width,
      anchorY: 0.78 * this.pet.style.height,
    });

    // bubble speech
    this.bubble = new ImageScaleView({
      superview: this.contentWrapper,
      image: `assets/ui/conversation/speechbubble.png`,
      imgScale: 0.66,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 44, right: 44 },
        vertical: { top: 44, bottom: 44 },
      },
      width: 360,
      height: 200,
      x: 280,
      y: 100,
    });

    new ImageView({
      superview: this.bubble,
      image: 'assets/ui/conversation/speechbubble_tail.png',
      width: 82,
      height: 67,
      y: 75,
      x: 10,
      r: Math.PI / 2,
      scale: 0.5,
    });

    new LangBitmapFontTextView({
      superview: this.bubble,
      size: 32,
      font: 'Body',
      color: '#985C25',
      localeText: () => i18n('match2.movesPinch.dontLose'),
      width: this.bubble.style.width,
      height: this.bubble.style.height,
      x: 0,
      y: -5,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
    });

    this.achievements = new FlexBoxImageScaleView({
      superview: this.contentWrapper,
      width: 540,
      height: 390,
      image: 'assets/ui/match2/common/list_scroll_area.png',
      flex: {
        flexDirection: 'column',
        alignContent: 'center',
        justifyContent: 'center',
      },
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 30, center: 30, right: 30 },
        vertical: { top: 30, middle: 30, bottom: 30 },
      },
      imgScale: 2,
      x: 75,
      y: 340,
    });

    this.ctaButton = new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: this.contentWrapper,
      width: 477,
      height: 110,
      centerOnOrigin: true,
      x: width * 0.5,
      y: height - 100,
      fontSize: 50,
      icon: {
        image: 'assets/ui/hud/icons/hud_gem.png',
        width: 54,
        height: 45,
        offset: { x: 190, y: 0 },
      },
      labelOffsetX: -35,
      localeText: () => i18n('match2.movesPinch.cta'),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_OK,
      },
    });

    this.toggleMessage = new LangBitmapFontTextShadowView({
      superview: this.root,
      font: 'Body',
      color: '#FFFFFF',
      width: this.root.style.width,
      height: 100,
      size: 30,
      x: this.root.style.width * 0.5,
      y: 150 + this.box.style.y + this.box.style.height * 0.5,
      localeText: () => i18n('match2.movesPinch.toggleMessage1'),
      centerOnOrigin: true,
      centerAnchor: true,
      verticalAlign: 'center',
      align: 'center',
      scaleY: 0,
      blockEvents: true,
      backgroundColor: 'rgba(0,0,0,0.7)',
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/conversation/speechbubble.png',
      'assets/ui/conversation/speechbubble_tail.png',
      'assets/ui/match2/common/list_scroll_area.png',
      'assets/ui/hud/icons/hud_gem.png',
      'assets/ui/match2/common/streak_bg.png',
    ]);
  }

  public init({
    price,
    objectivesCount,
    buruStreak,
  }: {
    price: number;
    objectivesCount?: number;
    buruStreak?: number;
  }) {
    super.init({ title: i18n(`match2.movesPinch.title3`) });

    const petAnimations = petAnimationNames[this.species];
    this.pet.setAnimation(petAnimations.cry);

    this.ctaButton.label.localeText = () =>
      `${i18n('match2.movesPinch.cta')}  ${price}`;
    this.ctaButton.onClick = async () => {
      sounds.sfx.play('ok');
      const success = await movesPinchCheck(price, 'match2_shop_moves');
      if (success) this.close(true);
    };

    this.addAchievements({ objectivesCount, buruStreak });
  }

  private addAchievements({
    objectivesCount,
    buruStreak,
  }: {
    objectivesCount?: number;
    buruStreak?: number;
  }) {
    const achievements: {
      view: View;
      lossAmount: number;
      animate: () => void;
    }[] = [];

    // Buru streak
    if (isMatch2UnlockableUnlocked(user(), 'buruStreak')) {
      buruStreak = (buruStreak ?? 0) + 1;
      const maxProgress = match2BuruStreakRewards.length;
      const streak = new Match2LiveOpsProgressbar({
        title: i18n('match2.buruStreak.title'),
        currentProgress: buruStreak,
        maxProgress,
      });

      const item = new ImageCoverView({
        width: 80,
        height: 80,
        mode: 'contain',
        image: 'assets/ui/match2/common/streak_bg.png',
        centerOnOrigin: true,
      });

      getStreakRewardIcon(
        getCurrentBuruStreakRewardSet(user()) ?? match2BuruStreakRewards[0],
      )
        .get()
        .then((image) => {
          new ImageScaleView({
            superview: item,
            image,
            width: item.style.width,
            height: item.style.height,
            centerOnOrigin: true,
            x: item.style.width * 0.5 - 2,
            y: item.style.height * 0.5 - 4,
            scale: 0.85,
          });
        });

      new LangBitmapFontTextView({
        superview: item,
        color: '#FFFFFF',
        font: 'Title',
        size: 21,
        localeText: () => `${buruStreak}x`,
        width: 30,
        height: 20,
        x: item.style.width - 27,
        y: 10,
        align: 'center',
        verticalAlign: 'center',
      });

      streak.updateIcon(item);

      achievements.push({
        view: streak,
        lossAmount: buruStreak / maxProgress,
        animate: () => streak.animateProgress(0),
      });
    }

    // Frenzy
    const frenzyEnabled = isMatch2FrenzyEnabled(user(), now());
    const config = getMatch2FrenzyEventConfig(user(), now());
    if (frenzyEnabled && config && objectivesCount) {
      objectivesCount = objectivesCount ?? 0;
      const maxProgress =
        getMatch2FrenzyMilestone(user(), now())?.requirement ?? 1;
      const existingProgress = getMatch2FrenzyMilestoneProgress(user(), now());
      const frenzy = new Match2LiveOpsProgressbar({
        title: i18n('match2.movesPinch.frenzy'),
        currentProgress: existingProgress + objectivesCount,
        earnedProgress: objectivesCount,
        accumulatedProgress: existingProgress,
        maxProgress,
      });

      getFrenzyObjectiveIcon(config.theme)
        .get()
        .then((image) => frenzy.updateIcon({ image }));

      achievements.push({
        view: frenzy,
        lossAmount: objectivesCount / maxProgress,
        animate: () => frenzy.animateProgress(existingProgress),
      });
    }

    // Fuji quest
    if (isStreakBattleRoyaleActive(user(), now())) {
      const playerPlace =
        getStreakBattleRoyaleCurrentMilestone(user(), now()) + 1;
      const maxProgress = getStreakBattleRoyaleGoal(user());

      const fuji = new Match2LiveOpsProgressbar({
        currentProgress: playerPlace,
        maxProgress,
        title: i18n('match2.movesPinch.fujiQuest'),
      });

      fuji.updateIcon({
        image: streakBattleRoyaleTheme().getAsset('retry_flag2'),
      });

      achievements.push({
        view: fuji,
        lossAmount: playerPlace / maxProgress,
        animate: () => fuji.animateProgress(0),
      });
    }

    // Most valuable achievements first
    achievements.sort((a, b) => b.lossAmount - a.lossAmount);

    // Add achievements to the view in order and animate them.
    achievements.forEach((achievement, index) => {
      const { view, animate } = achievement;
      view.updateOpts({
        superview: this.achievements,
      });

      waitForItPromise(1000 + 500 * index).then(() => {
        animate();
      });
    });

    // If no achievements, hide the achievements container, the bubble and center pet.
    if (!achievements.length) {
      const achievementsHeight = this.achievements.style.height;
      this.box.style.height -= achievementsHeight;
      this.box.style.y += achievementsHeight * 0.5;
      this.ctaButton.style.y -= achievementsHeight;
      this.achievements.removeFromSuperview();
      this.bubble?.removeFromSuperview();
      this.pet.updateOpts({
        x: this.contentWrapper.style.width * 0.5,
      });
    }
  }

  private async toggleShowPuzzle() {
    if (!this.toggleMessage) return;
    this.isPopupHidden = !this.isPopupHidden;

    // Toggle CTAs interactivity accordingly
    this.box.updateOpts({ blockEvents: this.isPopupHidden });

    const duration = 0.5;
    gsap.to(this.bg.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });
    gsap.to(this.box.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });

    await this.tweenToggleMessageScaleY(0);
    this.toggleMessage.localeText = () =>
      i18n(`match2.movesPinch.toggleMessage${this.isPopupHidden ? 2 : 1}`);
    await this.tweenToggleMessageScaleY(1);
  }

  private async tweenToggleMessageScaleY(v: number) {
    if (!this.toggleMessage) return;
    await gsap.to(this.toggleMessage.style, {
      duration: 0.15,
      scaleY: v,
      ease: 'quad.inOut',
      overwrite: true,
    });
  }

  public override async fadeIn() {
    super.fadeIn();
    await delay(animDuration * 2);
    await this.tweenToggleMessageScaleY(1);
  }

  public override async fadeOut() {
    void this.tweenToggleMessageScaleY(0);
    super.fadeOut();
  }
}
